import type {
  IAudioContext,
  IMediaStreamAudioSourceNode,
} from 'standardized-audio-context';
import type { StateCreator } from 'zustand';
import { captureFlags } from '../configure/flags-facade';
import { captureHostInputs } from '../configure/host-inputs-facade';
import { captureUserContext } from '../configure/user-context-facade';
import { getSafeErrorFields, isDeviceInUseDOMException } from '../errors';
import {
  DeviceInUseError,
  MicPermissionError,
  NoMicDevicesError,
  TabSharingError,
} from '../types';
import type { AudioGraphStore } from './types';

export interface SourceSlice {
  micSourceStream: MediaStream | null;
  tabSourceStream: MediaStream | null;
  micSourceNode: IMediaStreamAudioSourceNode<IAudioContext> | null;
  tabSourceNode: IMediaStreamAudioSourceNode<IAudioContext> | null;
  createMicSource: (signal?: AbortSignal) => Promise<void>;
  createTabSource: (signal?: AbortSignal) => Promise<void>;
  cleanupResourcesIfUnused: () => void;
  cleanupMicSource: (disconnectAnalyzer?: boolean) => void;
  cleanupTabSource: (disconnectAnalyzer?: boolean) => void;
  videoStream: MediaStream | null;
}

// Not always a DOMException: older Safari and Firefox reject with their own shape.
const isOverconstrainedError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  (err as { name?: unknown }).name === 'OverconstrainedError';

const isMicPermissionDOMException = (err: unknown): err is DOMException =>
  err instanceof DOMException &&
  (err.name === 'NotAllowedError' || err.name === 'SecurityError');

const isMicNotFoundDOMException = (err: unknown): err is DOMException =>
  err instanceof DOMException && err.name === 'NotFoundError';

// Records a getUserMedia rejection as a graph error so the mic UI shows it, and
// returns the typed error to throw in its place.
const recordMicFailure = (get: () => AudioGraphStore, err: unknown): unknown => {
  if (isDeviceInUseDOMException(err)) {
    // The typed error replaces the DOMException, so capture the original
    // name here — it's the only place NotReadableError (Chrome/Edge) vs
    // AbortError (Firefox) stays distinguishable for Datadog.
    get().logError({ event_type: 'MIC_DEVICE_IN_USE', details: { name: err.name } });
    get().addError('DEVICE_IN_USE');
    return new DeviceInUseError('microphone device in use', { cause: err });
  }
  if (isMicPermissionDOMException(err)) {
    get().logWarn({ event_type: 'MIC_PERMISSION_DENIED', details: { name: err.name } });
    // On desktop the WebView's answer is not the capture authority (APP-7701),
    // so its denial must not block the native mic.
    if (!captureHostInputs.isNativeCaptureHost()) get().blockMic();
    return new MicPermissionError('mic disallowed', { cause: err });
  }
  if (isMicNotFoundDOMException(err) || isOverconstrainedError(err)) {
    get().logWarn({
      event_type: 'MIC_DEVICE_NOT_FOUND',
      details: { name: getSafeErrorFields(err).errorName },
    });
    get().addError('NO_DEVICES');
    return new NoMicDevicesError('no mic devices available', { cause: err });
  }
  return err;
};

export const createSourceSlice: StateCreator<AudioGraphStore, [], [], SourceSlice> = (
  set,
  get
) => ({
  micSourceStream: null,
  tabSourceStream: null,
  micSourceNode: null,
  tabSourceNode: null,
  videoStream: null,

  cleanupMicSource: (disconnectAnalyzer: boolean = true) => {
    const sourceNode = get().micSourceNode;
    const sourceStream = get().micSourceStream;
    try {
      if (disconnectAnalyzer) sourceNode?.disconnect(get().micAnalyzer!);
      sourceNode?.disconnect();
    } catch (err) {
      console.trace(err);
      get().logError({
        event_type: 'CLEANUP_MIC_SOURCE_ERROR',
        details: getSafeErrorFields(err),
      });
    }
    try {
      sourceStream?.getTracks().forEach((track) => track.stop());
    } catch (err) {
      get().logError({
        event_type: 'CLEANUP_MIC_STREAM_ERROR',
        details: getSafeErrorFields(err),
      });
    }
  },

  cleanupTabSource: (disconnectAnalyzer: boolean = true) => {
    const sourceNode = get().tabSourceNode;
    const sourceStream = get().tabSourceStream;
    const videoStream = get().videoStream;
    try {
      if (disconnectAnalyzer) sourceNode?.disconnect(get().tabAnalyzer!);
      sourceNode?.disconnect();
    } catch (err) {
      get().logError({
        event_type: 'CLEANUP_TAB_SOURCE_ERROR',
        details: getSafeErrorFields(err),
      });
    }
    try {
      sourceStream?.getTracks().forEach((track) => track.stop());
      videoStream?.getTracks().forEach((track) => track.stop());
    } catch (err) {
      get().logError({
        event_type: 'CLEANUP_TAB_STREAM_ERROR',
        details: getSafeErrorFields(err),
      });
    }
  },

  createMicSource: async (signal) => {
    const enableDefaultTelehealthMode = captureFlags.isDefaultTelehealthModeEnabled();
    await get().loadInputDevices();
    if (signal?.aborted) throw signal.reason;
    // loadInputDevices re-ran the permission check; a failure there is already
    // recorded, and requesting the stream again would only repeat it.
    if (get().hasError()) return;
    const userData = captureUserContext.getCurrentUser();

    const deviceId = get().device?.deviceId;
    const audioConstraints: MediaTrackConstraints = { echoCancellation: true };

    if (enableDefaultTelehealthMode && userData && userData.session_settings) {
      audioConstraints.echoCancellation =
        !userData.session_settings.default_telehealth_mode;
    }

    const requestMicStream = (exactDeviceId: string | undefined) =>
      navigator.mediaDevices.getUserMedia({
        audio: {
          ...audioConstraints,
          deviceId: exactDeviceId ? { exact: exactDeviceId } : undefined,
        },
      });

    let micSourceStream: MediaStream;
    try {
      micSourceStream = await requestMicStream(deviceId);
    } catch (err) {
      if (!deviceId || !isOverconstrainedError(err)) throw recordMicFailure(get, err);
      // Safari rotates device ids, so a saved one can stop matching while the
      // mic itself is fine: fall back to the default device.
      get().logWarn({ event_type: 'MIC_SAVED_DEVICE_OVERCONSTRAINED' });
      try {
        micSourceStream = await requestMicStream(undefined);
      } catch (fallbackErr) {
        throw recordMicFailure(get, fallbackErr);
      }
      get().pickDevice();
    }
    if (signal?.aborted) {
      micSourceStream.getTracks().forEach((track) => track.stop());
      throw signal.reason;
    }
    get().clearError('DEVICE_IN_USE');

    const micSourceNode = get().audioContext!.createMediaStreamSource(micSourceStream);
    micSourceNode.connect(get().recorderDestinationNode!);
    if (get().micAnalyzer) micSourceNode.connect(get().micAnalyzer!);
    set({ micSourceStream, micSourceNode });
  },

  createTabSource: async (signal) => {
    if (signal?.aborted) throw signal.reason;
    // Chrome defaults selfBrowserSurface to 'exclude', hiding the tab Heidi runs
    // in so users can't pick their current tab as the telehealth audio source.
    // Gated rollout: when on, include it in the picker; off preserves the default.
    const selfBrowserSurface = captureFlags.isTelehealthAllowCurrentTabEnabled()
      ? 'include'
      : 'exclude';

    const videoStream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: 1,
        height: 1,
        frameRate: 1,
        displaySurface: 'browser',
      },
      audio: {
        channelCount: 2,
        noiseSuppression: false,
        autoGainControl: false,
        echoCancellation: false,
      },
      // Experimental properties: MDN defines them but they throw where they are
      // new, and browsers that do not know them ignore them.
      // @ts-expect-error see above
      systemAudio: 'exclude',
      windowAudio: 'exclude',
      selfBrowserSurface,
    });

    if (signal?.aborted) {
      videoStream.getTracks().forEach((track) => track.stop());
      throw signal.reason;
    }

    if (videoStream.getAudioTracks().length == 0) {
      videoStream.getTracks().forEach((t) => t.stop());
      throw new TabSharingError('No audio tracks in tab stream');
    }

    const tabSourceStream = new MediaStream();
    videoStream
      .getAudioTracks()
      .forEach((audioTrack) => tabSourceStream.addTrack(audioTrack));

    const tabSourceNode = get().audioContext!.createMediaStreamSource(tabSourceStream);
    tabSourceNode.connect(get().recorderDestinationNode!);
    if (get().tabAnalyzer) tabSourceNode.connect(get().tabAnalyzer!);
    set({ tabSourceStream, tabSourceNode, videoStream });
  },

  cleanupResourcesIfUnused: () => {
    const { listeners, micAnalyzerUsers, tabAnalyzerUsers } = get();

    if (listeners.length === 0) {
      if (micAnalyzerUsers.size === 0) {
        get().cleanupMicSource();
      }
      if (tabAnalyzerUsers.size === 0) {
        get().cleanupTabSource();
      }
    }
  },
});
